import React, { useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';
import type { Board, SquareWithNumbers, Hit, ThemeData, ThemeDecoration, NumberMode, BoxNeighborSize, CellMode, CellShape, TeamMapping } from '../types';
import { DEFAULT_THEME_DATA, getNumberSetKeys } from '../types';
import {
  getMergedGridDimensions,
  getFirstSquareForVisualPosition,
  getSquarePositionsForVisualCell,
  getMergedCellSize,
  getNumbersForMergedHeaderCell,
  getCellNumberForSquarePosition,
} from '../utils/gridUtils';
import logoPng from '../assets/logo.png';

// Get box border style for a cell based on its position within a box group
// Uses actual CSS borders for html2canvas compatibility (box-shadow inset doesn't render in html2canvas)
const getBoxBorderStyle = (
  row: number,
  col: number,
  boxSize: BoxNeighborSize,
  borderColor: string,
  borderWidth: number,
  defaultBorder: string // The default cell border (e.g., "1px solid #e0e0e0")
): React.CSSProperties => {
  let isTopEdge: boolean;
  let isBottomEdge: boolean;
  let isLeftEdge: boolean;
  let isRightEdge: boolean;

  if (boxSize === '2x2') {
    // 2x2 boxes: divide into 5x5 grid of boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 2 === 0;
    isRightEdge = col % 2 === 1;
  } else if (boxSize === '5x5') {
    // 5x5 quadrants: 4 quadrants (top-left, top-right, bottom-left, bottom-right)
    isTopEdge = row % 5 === 0;
    isBottomEdge = row % 5 === 4;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  } else {
    // 5x2 boxes: 2 columns of 5-wide boxes, 5 rows of 2-tall boxes
    isTopEdge = row % 2 === 0;
    isBottomEdge = row % 2 === 1;
    isLeftEdge = col % 5 === 0;
    isRightEdge = col % 5 === 4;
  }

  // Use actual CSS borders instead of box-shadow for html2canvas compatibility
  const boxBorder = `${borderWidth}px solid ${borderColor}`;

  return {
    borderTop: isTopEdge ? boxBorder : defaultBorder,
    borderBottom: isBottomEdge ? boxBorder : defaultBorder,
    borderLeft: isLeftEdge ? boxBorder : defaultBorder,
    borderRight: isRightEdge ? boxBorder : defaultBorder,
  };
};

// Helper to get position styles from decoration position for export
const getDecorationPositionStyles = (
  position: ThemeDecoration['position'],
  offsetX?: string,
  offsetY?: string
): React.CSSProperties => {
  const baseStyles: React.CSSProperties = { position: 'absolute' };

  switch (position) {
    case 'top-left':
      return { ...baseStyles, top: offsetY || '0', left: offsetX || '0' };
    case 'top-right':
      return { ...baseStyles, top: offsetY || '0', right: offsetX?.replace('-', '') || '0' };
    case 'bottom-left':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', left: offsetX || '0' };
    case 'bottom-right':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', right: offsetX?.replace('-', '') || '0' };
    case 'top':
      return { ...baseStyles, top: offsetY || '0', left: '50%', transform: 'translateX(-50%)' };
    case 'bottom':
      return { ...baseStyles, bottom: offsetY?.replace('-', '') || '0', left: '50%', transform: 'translateX(-50%)' };
    case 'left':
      return { ...baseStyles, left: offsetX || '0', top: '50%', transform: 'translateY(-50%)' };
    case 'right':
      return { ...baseStyles, right: offsetX?.replace('-', '') || '0', top: '50%', transform: 'translateY(-50%)' };
    default:
      return baseStyles;
  }
};

// Render a single decoration for export
const ExportDecoration: React.FC<{ decoration: ThemeDecoration }> = ({ decoration }) => {
  const positionStyles = getDecorationPositionStyles(decoration.position, decoration.offsetX, decoration.offsetY);

  if (decoration.type === 'emoji') {
    return (
      <Box
        sx={{
          ...positionStyles,
          fontSize: decoration.size || '40px',
          opacity: decoration.opacity ?? 1,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 1,
        }}
      >
        {decoration.content}
      </Box>
    );
  }

  if (decoration.type === 'image') {
    return (
      <Box
        component="img"
        src={decoration.content}
        alt=""
        sx={{
          ...positionStyles,
          width: decoration.size || '40px',
          height: 'auto',
          opacity: decoration.opacity ?? 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
    );
  }

  return null;
};

// Helper to get contrasting text color based on background
const getContrastColor = (bgColor: string): string => {
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Helper to render an icon by name
const renderIcon = (name: string, size: number = 18) => {
  const IconComponent = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

// Get quarter key label (Q1, 1H, P1, etc.)
const getQuarterKeyLabel = (key: string, quarters: number): string => {
  if (quarters === 2) {
    if (key === '1H') return '1st Half';
    if (key === '2H') return '2nd Half';
  }
  if (quarters === 3) {
    if (key === 'P1') return '1st Period';
    if (key === 'P2') return '2nd Period';
    if (key === 'P3') return '3rd Period';
  }
  // Default for 4 quarters
  if (key === 'Q1') return 'Q1';
  if (key === 'Q2') return 'Q2 / Halftime';
  if (key === 'Q3') return 'Q3';
  if (key === 'Q4') return 'Q4 / Final';
  return key;
};

// Get ordered quarter keys based on number of quarters
const getOrderedQuarterKeys = (quarters: number): string[] => {
  if (quarters === 2) return ['1H', '2H'];
  if (quarters === 3) return ['P1', 'P2', 'P3'];
  return ['Q1', 'Q2', 'Q3', 'Q4'];
};

interface BoardExportProps {
  board: Board;
  grid: SquareWithNumbers[][];
  hits?: Hit[];
  buttonText?: string;
  themeData?: ThemeData;
  iconOnly?: boolean;
  iconColor?: string;
  hideNames?: boolean;
  teamMappings?: TeamMapping[];
}

export const BoardExport: React.FC<BoardExportProps> = ({ board, grid, hits = [], buttonText = 'Export Image', themeData: providedThemeData, iconOnly = false, iconColor, hideNames = false, teamMappings = [] }) => {
  // Merge theme data with defaults so missing properties fall back to defaults
  const rawThemeData = providedThemeData || board.theme?.themeData;
  const themeData = rawThemeData ? { ...DEFAULT_THEME_DATA, ...rawThemeData } : DEFAULT_THEME_DATA;
  // Check if a theme is actually applied (not just defaults)
  const hasTheme = Boolean(providedThemeData || board.theme);
  const exportRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = React.useState(false);

  // Count hits per square
  const hitCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    hits.forEach((h) => {
      const key = `${h.winningRow}-${h.winningCol}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [hits]);

  const getHitCount = (row: number, col: number): number => {
    return hitCounts.get(`${row}-${col}`) || 0;
  };

  const getSquareColor = (square: SquareWithNumbers | undefined, row: number, col: number) => {
    if (!square) return themeData.squareUnclaimed;

    const hitCount = getHitCount(row, col);
    if (hitCount > 0) return themeData.squareWinner;

    return themeData.cellBackground;
  };

  // Cell mode settings (defined early so getSquareLabel can use them)
  const cellMode = (board.cellMode || 'standard') as CellMode;
  const cellShape = (board.cellShape || '1x1') as CellShape;

  const getSquareLabel = (square: SquareWithNumbers | undefined, row: number, col: number): { icon?: React.ReactNode; handle?: string } => {
    if (!square) return {};
    if (square.label) return { handle: square.label };

    // If hideNames is enabled, don't show player info for claimed squares
    if (hideNames && (square.offlinePlayer || square.owner || square.ownerId || square.offlinePlayerId)) {
      return {}; // Return empty - no icon or handle shown
    }

    // Check for offline player first
    if (square.offlinePlayer) {
      const icon = square.offlinePlayer.iconName ? renderIcon(square.offlinePlayer.iconName, 14) : null;
      const handle = square.offlinePlayer.handle?.toUpperCase() || square.offlinePlayer.displayName.substring(0, 3).toUpperCase();
      return { icon: icon || undefined, handle };
    }

    // Check for owner
    if (square.owner) {
      const icon = square.owner.iconName ? renderIcon(square.owner.iconName, 14) : null;
      const handle = square.owner.handle?.toUpperCase() || square.owner.displayName.substring(0, 3).toUpperCase();
      return { icon: icon || undefined, handle };
    }

    // Show cell number for unclaimed cells (accounts for cell modes)
    const cellNumber = getCellNumberForSquarePosition(row, col, cellMode, cellShape);
    return { handle: cellNumber.toString() };
  };

  const handleExport = useCallback(async () => {
    if (!exportRef.current) return;

    setExporting(true);
    try {
      const canvas = await html2canvas(exportRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          // Use board slug for filename
          const slug = board.slug || board.id;
          link.download = `squareup-${slug}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Failed to export board:', error);
    } finally {
      setExporting(false);
    }
  }, [board.slug, board.id]);

  // Team positioning
  const homeOnTop = board.homeOnTop ?? true;
  const topTeam = homeOnTop ? board.homeTeam : board.awayTeam;
  const leftTeam = homeOnTop ? board.awayTeam : board.homeTeam;
  const topNumbers = homeOnTop ? board.homeNumbers : board.awayNumbers;
  const leftNumbers = homeOnTop ? board.awayNumbers : board.homeNumbers;

  // Multi-number mode support
  const numberMode: NumberMode = board.numberMode || 'single';
  const numberSetKeys = getNumberSetKeys(board.quarters || 4, numberMode);
  const hasMultipleNumberSets = numberSetKeys.length > 0;

  // Helper to get numbers for a specific number set key (top = home if homeOnTop, else away)
  const getTopNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
    }
    return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
  };

  const getLeftNumbersForKey = (key: string): number[] => {
    if (homeOnTop) {
      return board.quarterAwayNumbers?.[key] ?? board.awayNumbers;
    }
    return board.quarterHomeNumbers?.[key] ?? board.homeNumbers;
  };

  // Resolve team mappings for live game boards
  const homeTeamMapping = teamMappings.find(t => t.teamName === board.homeTeam || t.abbreviation === board.homeTeam);
  const awayTeamMapping = teamMappings.find(t => t.teamName === board.awayTeam || t.abbreviation === board.awayTeam);
  const topTeamLogoUrl = homeOnTop ? homeTeamMapping?.logoUrl : awayTeamMapping?.logoUrl;
  const leftTeamLogoUrl = homeOnTop ? awayTeamMapping?.logoUrl : homeTeamMapping?.logoUrl;

  // For live boards with classic/no theme, use actual team colors
  const isClassicOrNoTheme = !board.themeId || board.theme?.lookupKey === 'classic';
  const liveHomeColor = isClassicOrNoTheme && homeTeamMapping?.primaryColor ? homeTeamMapping.primaryColor : undefined;
  const liveAwayColor = isClassicOrNoTheme && awayTeamMapping?.primaryColor ? awayTeamMapping.primaryColor : undefined;

  const homeColor = liveHomeColor || (board.themeId ? themeData.homeTeamColor : (board.homeColor || themeData.homeTeamColor));
  const awayColor = liveAwayColor || (board.themeId ? themeData.awayTeamColor : (board.awayColor || themeData.awayTeamColor));
  const topColor = homeOnTop ? homeColor : awayColor;
  const leftColor = homeOnTop ? awayColor : homeColor;

  const cellSize = 38;
  const headerSize = 30;
  const gap = 2; // Gap between cells

  // Derived cell mode dimensions
  const { rows: visualRows, cols: visualCols, rowSpan, colSpan } = getMergedGridDimensions(cellMode, cellShape);
  const { width: mergedCellWidth, height: mergedCellHeight } = getMergedCellSize(cellSize, gap, cellMode, cellShape);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format export date
  const formatExportDate = () => {
    return new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Calculate payout info
  const totalPot = board.pricePerSquare * (board.totalCells || 100);
  const profitPercent = board.payoutConfig?.profitPercent ?? 10;
  const profitAmount = totalPot * (profitPercent / 100);
  const payoutPot = totalPot - profitAmount;
  const quarterPayouts = board.payoutConfig?.quarterPayouts || {};
  const quarterWrongWayPayouts = board.payoutConfig?.quarterWrongWayPayouts;
  const quarterPlusNPayouts = board.payoutConfig?.quarterPlusNPayouts;
  const customCategories = board.payoutConfig?.customCategories;
  const orderedQuarterKeys = getOrderedQuarterKeys(board.quarters || 4);

  // Get owner handle
  const ownerHandle = board.owner?.handle || board.owner?.displayName;

  // 16:9 aspect ratio
  const exportWidth = 960;
  const exportHeight = 540;

  // Calculate grid dimensions based on cell mode
  const gridCellsWidth = cellMode !== 'standard'
    ? mergedCellWidth * visualCols + gap * (visualCols - 1)
    : cellSize * 10 + gap * 9;
  const numHeaderCols = hasMultipleNumberSets ? numberSetKeys.length : 1;
  const headerColWidth = hasMultipleNumberSets ? headerSize * 0.7 : headerSize;
  const gridWidth = headerSize + (numHeaderCols * (headerColWidth + gap)) + gridCellsWidth + gap;

  return (
    <Box>
      {iconOnly ? (
        <Tooltip title={buttonText}>
          <IconButton
            onClick={handleExport}
            disabled={exporting}
            size="small"
            sx={iconColor ? { color: iconColor } : undefined}
          >
            {exporting ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={exporting}
          size="small"
        >
          {exporting ? 'Downloading...' : buttonText}
        </Button>
      )}

      {/* Hidden exportable content */}
      <Box sx={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <Box
          ref={exportRef}
          sx={{
            width: exportWidth,
            height: exportHeight,
            backgroundColor: themeData.pageBackground || '#ffffff',
            background: themeData.pageGradient || themeData.pageBackground || '#ffffff',
            backgroundImage: themeData.backgroundImage ? `url(${themeData.backgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {/* Background overlay */}
          {themeData.backgroundOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: themeData.backgroundOverlay,
                zIndex: 0,
              }}
            />
          )}

          {/* Theme decorations (static, no animations) */}
          {themeData.decorations && themeData.decorations.length > 0 &&
            themeData.decorations.map((decoration, index) => (
              <ExportDecoration key={index} decoration={decoration} />
            ))
          }
          {/* Main Content Area - Two Column Layout: Info Left, Board Right */}
          {/* Text colors based on theme's textColorMode */}
          {(() => {
            const isLightText = themeData.textColorMode === 'light';
            const textPrimary = isLightText ? '#ffffff' : '#000000';
            const textSecondary = isLightText ? 'rgba(255,255,255,0.85)' : '#666666';
            const textTertiary = isLightText ? 'rgba(255,255,255,0.7)' : '#888888';
            const tableBorder = isLightText ? 'rgba(255,255,255,0.3)' : '#e0e0e0';
            const tableHeader = isLightText ? 'rgba(255,255,255,0.15)' : '#f5f5f5';
            const footerBorder = isLightText ? 'rgba(255,255,255,0.2)' : '#eeeeee';
            const footerText = isLightText ? 'rgba(255,255,255,0.6)' : '#999999';
            // Background panel for left side info - use theme's infoPanelBackground or fallback
            const defaultInfoPanelBg = isLightText ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.75)';
            const infoPanelBg = hasTheme
              ? (themeData.infoPanelBackground || defaultInfoPanelBg)
              : 'transparent';

            return (
          <>
          <Box sx={{ display: 'flex', flex: 1, gap: 3, position: 'relative', zIndex: 2 }}>
            {/* Left Side - Details */}
            <Box sx={{
              width: exportWidth - gridWidth - 60,
              display: 'flex',
              flexDirection: 'column',
              ...(hasTheme && {
                backgroundColor: infoPanelBg,
                borderRadius: 2,
                p: 1.5,
              }),
            }}>
              {/* Board Info Header - Two column layout */}
              <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                {/* Left: Name and Date */}
                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0.25, lineHeight: 1.2, color: textPrimary }}>
                    {board.name}
                  </Typography>
                  <Typography sx={{ color: textSecondary, fontSize: '0.75rem' }}>
                    {formatDate(board.gameDate)}
                  </Typography>
                </Box>
                {/* Right: General Info */}
                <Box sx={{ flex: '0 0 auto', textAlign: 'right' }}>
                  <Typography sx={{ color: textSecondary, fontSize: '0.7rem' }}>
                    ${board.pricePerSquare}/square
                  </Typography>
                  <Typography sx={{ color: textSecondary, fontSize: '0.7rem' }}>
                    Total: ${totalPot.toFixed(0)} &bull; Payout: ${payoutPot.toFixed(0)}
                  </Typography>
                  {ownerHandle && (
                    <Typography sx={{ color: textTertiary, fontSize: '0.65rem' }}>
                      @{ownerHandle.toUpperCase()}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Description - Full width row */}
              {board.description && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ color: textSecondary, fontSize: '0.7rem', fontStyle: 'italic', lineHeight: 1.4 }}>
                    {board.description.replace(/<[^>]*>/g, '').substring(0, 180)}
                    {board.description.length > 180 ? '...' : ''}
                  </Typography>
                </Box>
              )}

              {/* Payouts Table */}
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem', mb: 0.5, color: textPrimary }}>
                  Payouts
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.65rem',
                    color: textPrimary,
                    '& th, & td': {
                      border: `1px solid ${tableBorder}`,
                      px: 0.5,
                      py: 0.25,
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: tableHeader,
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Main</th>
                      {quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0) && (
                        <th>Wrong Way</th>
                      )}
                      {quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0) && (
                        <th>+{board.payoutConfig?.plusNOffset ?? 5}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {orderedQuarterKeys.map((key) => {
                      const mainAmount = quarterPayouts[key] || 0;
                      const wrongWayAmount = quarterWrongWayPayouts?.[key] || 0;
                      const plusNAmount = quarterPlusNPayouts?.[key] || 0;
                      const hasWrongWayColumn = quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0);
                      const hasPlusNColumn = quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0);

                      return (
                        <tr key={key}>
                          <td>{getQuarterKeyLabel(key, board.quarters || 4)}</td>
                          <td>${mainAmount.toFixed(0)}</td>
                          {hasWrongWayColumn && <td>{wrongWayAmount > 0 ? `$${wrongWayAmount.toFixed(0)}` : '-'}</td>}
                          {hasPlusNColumn && <td>{plusNAmount > 0 ? `$${plusNAmount.toFixed(0)}` : '-'}</td>}
                        </tr>
                      );
                    })}
                    {/* OT row if enabled */}
                    {board.payoutConfig?.overtimeConfig?.enabled && (
                      <tr>
                        <td>Final (OT)</td>
                        <td>${(board.payoutConfig.overtimeConfig.overtimeAmount ?? 0).toFixed(0)}</td>
                        {quarterWrongWayPayouts && Object.values(quarterWrongWayPayouts).some(v => v > 0) && <td>-</td>}
                        {quarterPlusNPayouts && Object.values(quarterPlusNPayouts).some(v => v > 0) && <td>-</td>}
                      </tr>
                    )}
                  </tbody>
                </Box>

                {/* Custom Payouts - horizontal layout */}
                {customCategories && customCategories.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.65rem', mb: 0.25, color: textPrimary }}>
                      Custom Payouts
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {customCategories.map((cat, idx) => (
                        <Typography key={cat.id} sx={{ color: textSecondary, fontSize: '0.6rem' }}>
                          {cat.name}: ${cat.payoutAmount.toFixed(0)}
                          {cat.wrongWayEnabled && ' (2-way)'}
                          {idx < customCategories.length - 1 && ' •'}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Neighbor Payouts */}
                {board.payoutConfig?.neighborPayouts?.enabled && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.65rem', mb: 0.25, color: textPrimary }}>
                      Neighbor Payouts
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: '0.6rem' }}>
                      {(() => {
                        const neighborConfig = board.payoutConfig.neighborPayouts;
                        const directions = neighborConfig.directions || [];
                        const directionLabel = directions.length === 8
                          ? 'All 8 directions'
                          : directions.length === 4
                            ? directions.every(d => ['N', 'E', 'S', 'W'].includes(d))
                              ? 'Cardinal (N, E, S, W)'
                              : 'Diagonal (NE, SE, SW, NW)'
                            : `${directions.length} direction${directions.length !== 1 ? 's' : ''}: ${directions.join(', ')}`;

                        if (neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts) {
                          const amounts = orderedQuarterKeys.map(key =>
                            `${key}: $${neighborConfig.quarterAmounts![key] || 0}`
                          ).join(' • ');
                          return `${directionLabel} | ${amounts}`;
                        }
                        return `${directionLabel} | $${neighborConfig.globalAmount || 0} per neighbor`;
                      })()}
                    </Typography>
                  </Box>
                )}

                {/* Box Neighbor Payouts */}
                {board.payoutConfig?.boxNeighborPayouts?.enabled && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.65rem', mb: 0.25, color: textPrimary }}>
                      Box Neighbor Payouts
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: '0.6rem' }}>
                      {(() => {
                        const boxConfig = board.payoutConfig.boxNeighborPayouts;
                        const sizeLabel = boxConfig.boxSize === '2x2' ? '2×2 boxes' : boxConfig.boxSize === '5x5' ? '5×5 quadrants' : '5×2 boxes';

                        if (boxConfig.usePerQuarterAmounts && boxConfig.quarterAmounts) {
                          const amounts = orderedQuarterKeys.map(key =>
                            `${key}: $${boxConfig.quarterAmounts![key] || 0}`
                          ).join(' • ');
                          return `${sizeLabel} | ${amounts}`;
                        }
                        return `${sizeLabel} | $${boxConfig.globalAmount || 0} per neighbor`;
                      })()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Game Log */}
              {hits.length > 0 && (
                <>
                  <Divider sx={{ my: 1, borderColor: tableBorder }} />
                  <Box>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem', mb: 0.5, color: textPrimary }}>
                      Game Log ({board.awayTeam} @ {board.homeTeam})
                    </Typography>
                    {(() => {
                      const displayHits = hits.filter(hit => !hit.isWrongWay && !hit.isPlusN);
                      const periodHits = displayHits.filter(h => h.quarter !== null && h.quarter !== undefined && !h.categoryId);
                      const finalHit = periodHits.find(h => h.quarter === 0);
                      const regularPeriodHits = periodHits.filter(h => h.quarter !== 0);
                      const customHits = displayHits.filter(h => h.categoryId);

                      // Build ordered pairs for table: Q1-Q2, Q3-Q4 (or 1H-2H, P1-P2-P3), Final-Custom
                      const rows: { left: { label: string; score: string } | null; right: { label: string; score: string } | null }[] = [];

                      if (board.quarters === 2) {
                        // Soccer: 1H - 2H, Final - Custom
                        const h1 = regularPeriodHits.find(h => h.quarter === 1);
                        const h2 = regularPeriodHits.find(h => h.quarter === 2);
                        if (h1 || h2) {
                          rows.push({
                            left: h1 ? { label: '1H', score: `${h1.awayScore} - ${h1.homeScore}` } : null,
                            right: h2 ? { label: '2H', score: `${h2.awayScore} - ${h2.homeScore}` } : null,
                          });
                        }
                      } else if (board.quarters === 3) {
                        // Hockey: P1 - P2, P3 - Final
                        const p1 = regularPeriodHits.find(h => h.quarter === 1);
                        const p2 = regularPeriodHits.find(h => h.quarter === 2);
                        const p3 = regularPeriodHits.find(h => h.quarter === 3);
                        if (p1 || p2) {
                          rows.push({
                            left: p1 ? { label: 'P1', score: `${p1.awayScore} - ${p1.homeScore}` } : null,
                            right: p2 ? { label: 'P2', score: `${p2.awayScore} - ${p2.homeScore}` } : null,
                          });
                        }
                        if (p3 || finalHit) {
                          rows.push({
                            left: p3 ? { label: 'P3', score: `${p3.awayScore} - ${p3.homeScore}` } : null,
                            right: finalHit ? { label: 'Final', score: `${finalHit.awayScore} - ${finalHit.homeScore}` } : null,
                          });
                        }
                      } else {
                        // Football/Basketball: Q1 - Q2, Q3 - Q4, Final - Custom
                        const q1 = regularPeriodHits.find(h => h.quarter === 1);
                        const q2 = regularPeriodHits.find(h => h.quarter === 2);
                        const q3 = regularPeriodHits.find(h => h.quarter === 3);
                        const q4 = regularPeriodHits.find(h => h.quarter === 4);
                        if (q1 || q2) {
                          rows.push({
                            left: q1 ? { label: 'Q1', score: `${q1.awayScore} - ${q1.homeScore}` } : null,
                            right: q2 ? { label: 'Q2', score: `${q2.awayScore} - ${q2.homeScore}` } : null,
                          });
                        }
                        if (q3 || q4) {
                          rows.push({
                            left: q3 ? { label: 'Q3', score: `${q3.awayScore} - ${q3.homeScore}` } : null,
                            right: q4 ? { label: 'Q4', score: `${q4.awayScore} - ${q4.homeScore}` } : null,
                          });
                        }
                      }

                      // Add Final row (for 2 and 4 quarter games) and custom hits
                      if (board.quarters !== 3) {
                        // For non-hockey, Final goes in the next row
                        const firstCustom = customHits[0];
                        if (finalHit || firstCustom) {
                          rows.push({
                            left: finalHit ? { label: 'Final', score: `${finalHit.awayScore} - ${finalHit.homeScore}` } : null,
                            right: firstCustom ? {
                              label: customCategories?.find(c => c.id === firstCustom.categoryId)?.name || 'Custom',
                              score: `${firstCustom.awayScore} - ${firstCustom.homeScore}`
                            } : null,
                          });
                        }
                        // Add remaining custom hits
                        for (let i = 1; i < customHits.length; i += 2) {
                          const c1 = customHits[i];
                          const c2 = customHits[i + 1];
                          rows.push({
                            left: c1 ? {
                              label: customCategories?.find(c => c.id === c1.categoryId)?.name || 'Custom',
                              score: `${c1.awayScore} - ${c1.homeScore}`
                            } : null,
                            right: c2 ? {
                              label: customCategories?.find(c => c.id === c2.categoryId)?.name || 'Custom',
                              score: `${c2.awayScore} - ${c2.homeScore}`
                            } : null,
                          });
                        }
                      } else {
                        // Hockey: custom hits after Final
                        for (let i = 0; i < customHits.length; i += 2) {
                          const c1 = customHits[i];
                          const c2 = customHits[i + 1];
                          rows.push({
                            left: c1 ? {
                              label: customCategories?.find(c => c.id === c1.categoryId)?.name || 'Custom',
                              score: `${c1.awayScore} - ${c1.homeScore}`
                            } : null,
                            right: c2 ? {
                              label: customCategories?.find(c => c.id === c2.categoryId)?.name || 'Custom',
                              score: `${c2.awayScore} - ${c2.homeScore}`
                            } : null,
                          });
                        }
                      }

                      return (
                        <Box
                          component="table"
                          sx={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.65rem',
                            color: textSecondary,
                            '& td': {
                              py: 0.25,
                              px: 0.5,
                            },
                          }}
                        >
                          <tbody>
                            {rows.map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ width: '50%' }}>
                                  {row.left && (
                                    <span>
                                      <strong>{row.left.label}:</strong> {row.left.score}
                                    </span>
                                  )}
                                </td>
                                <td style={{ width: '50%' }}>
                                  {row.right && (
                                    <span>
                                      <strong>{row.right.label}:</strong> {row.right.score}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      );
                    })()}
                  </Box>
                </>
              )}

            </Box>

            {/* Right Side - Grid */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
              {/* Grid Container */}
              <Box sx={{ display: 'inline-block' }}>
                {/* Top Team Name */}
                <Box sx={{
                  display: 'flex',
                  mb: 0.25,
                  ml: hasMultipleNumberSets
                    ? `${headerSize + (numberSetKeys.length * (headerSize * 0.7 + 1))}px` // Team label + (column width + margin) * num columns
                    : `${headerSize + headerSize}px` // Single mode: team label + one number column
                }}>
                  <Box
                    sx={{
                      width: gridCellsWidth + gap * 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    {topTeamLogoUrl && (
                      <img
                        src={topTeamLogoUrl}
                        alt={topTeam}
                        style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                        crossOrigin="anonymous"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: topColor,
                        fontSize: '0.7rem',
                      }}
                    >
                      {topTeam}
                    </Typography>
                  </Box>
                </Box>

                {/* Top Numbers Row(s) - single for 'single' mode, multiple for 'half'/'quarter' modes */}
                {!hasMultipleNumberSets ? (
                  <Box sx={{ display: 'flex', mb: '2px' }}>
                    <Box sx={{ width: headerSize }} />
                    <Box sx={{ width: headerSize }} />
                    {/* For cell modes, render merged header cells with multiple numbers */}
                    {cellMode !== 'standard' ? (
                      Array.from({ length: visualCols }, (_, visualColIndex) => {
                        const numbersInCell = getNumbersForMergedHeaderCell(topNumbers, visualColIndex, colSpan);
                        return (
                          <Box
                            key={visualColIndex}
                            sx={{
                              width: mergedCellWidth,
                              height: headerSize,
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'stretch',
                              justifyContent: 'stretch',
                              ml: '2px',
                            }}
                          >
                            {board.numbersRevealed ? (
                              numbersInCell.map((num, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: themeData.headerBackground,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    borderRight: i < numbersInCell.length - 1 ? 'none' : undefined,
                                    borderLeft: i > 0 ? 'none' : undefined,
                                    color: topColor,
                                    fontWeight: themeData.headerFontWeight,
                                    fontFamily: themeData.fontFamily,
                                    fontSize: '0.65rem',
                                  }}
                                >
                                  {num}
                                </Box>
                              ))
                            ) : (
                              <Box
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: themeData.headerBackground,
                                  border: `1px solid ${themeData.cellBorder}`,
                                  color: topColor,
                                  fontWeight: themeData.headerFontWeight,
                                  fontFamily: themeData.fontFamily,
                                }}
                              >
                                -
                              </Box>
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      topNumbers.map((num, col) => (
                        <Box
                          key={col}
                          sx={{
                            width: cellSize,
                            height: headerSize,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: themeData.headerBackground,
                            border: `1px solid ${themeData.cellBorder}`,
                            color: topColor,
                            fontWeight: themeData.headerFontWeight,
                            fontFamily: themeData.fontFamily,
                            fontSize: '0.65rem',
                            ml: '2px',
                          }}
                        >
                          {board.numbersRevealed ? num : '-'}
                        </Box>
                      ))
                    )}
                  </Box>
                ) : (
                  // Multi-number mode - reverse order so Q1/1H is closest to the grid (inside) and Q4/2H is furthest (outside)
                  <Box sx={{ mb: '2px' }}>
                    {[...numberSetKeys].reverse().map((key) => {
                      const numbersForKey = getTopNumbersForKey(key);
                      return (
                        <Box key={key} sx={{ display: 'flex', mb: '1px' }}>
                          {/* Left team label spacer */}
                          <Box sx={{ width: headerSize }} />
                          {/* Spacer for ALL left number columns */}
                          <Box sx={{ width: numberSetKeys.length * (headerSize * 0.7 + 1) }} />
                          {/* Number cells - for cell modes, render merged header cells with multiple numbers */}
                          {cellMode !== 'standard' ? (
                            Array.from({ length: visualCols }, (_, visualColIndex) => {
                              const numbersInCell = getNumbersForMergedHeaderCell(numbersForKey, visualColIndex, colSpan);
                              return (
                                <Box
                                  key={visualColIndex}
                                  sx={{
                                    width: mergedCellWidth,
                                    height: headerSize * 0.7,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'stretch',
                                    justifyContent: 'stretch',
                                    ml: '2px',
                                  }}
                                >
                                  {board.numbersRevealed ? (
                                    numbersInCell.map((num, i) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: themeData.headerBackground,
                                          border: `1px solid ${themeData.cellBorder}`,
                                          borderRight: i < numbersInCell.length - 1 ? 'none' : undefined,
                                          borderLeft: i > 0 ? 'none' : undefined,
                                          color: topColor,
                                          fontWeight: themeData.headerFontWeight,
                                          fontFamily: themeData.fontFamily,
                                          fontSize: '0.55rem',
                                        }}
                                      >
                                        {num}
                                      </Box>
                                    ))
                                  ) : (
                                    <Box
                                      sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: themeData.headerBackground,
                                        border: `1px solid ${themeData.cellBorder}`,
                                        color: topColor,
                                        fontWeight: themeData.headerFontWeight,
                                        fontFamily: themeData.fontFamily,
                                      }}
                                    >
                                      -
                                    </Box>
                                  )}
                                </Box>
                              );
                            })
                          ) : (
                            numbersForKey.map((num, col) => (
                              <Box
                                key={col}
                                sx={{
                                  width: cellSize,
                                  height: headerSize * 0.7,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: themeData.headerBackground,
                                  border: `1px solid ${themeData.cellBorder}`,
                                  color: topColor,
                                  fontWeight: themeData.headerFontWeight,
                                  fontFamily: themeData.fontFamily,
                                  fontSize: '0.55rem',
                                  ml: '2px',
                                }}
                              >
                                {board.numbersRevealed ? num : '-'}
                              </Box>
                            ))
                          )}
                          {/* Period label at the end */}
                          <Box
                            sx={{
                              width: headerSize * 0.5,
                              height: headerSize * 0.7,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.5rem',
                              fontWeight: 'bold',
                              color: topColor,
                              ml: '2px',
                            }}
                          >
                            {key}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Main Grid with Left Team */}
                <Box sx={{ display: 'flex' }}>
                  {/* Left Team Label - render each character vertically */}
                  <Box
                    sx={{
                      width: headerSize,
                      // Height spans Q label row + grid cells for multi-mode
                      height: cellMode !== 'standard'
                        ? mergedCellHeight * visualRows + gap * (visualRows - 1) + (hasMultipleNumberSets ? headerSize * 0.5 + 1 : 0)
                        : cellSize * 10 + 18 + (hasMultipleNumberSets ? headerSize * 0.5 + 1 : 0),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {leftTeam.split('').map((char, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontWeight: 'bold',
                          color: leftColor,
                          fontSize: '0.6rem',
                          lineHeight: 1.1,
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                    {leftTeamLogoUrl && (
                      <img
                        src={leftTeamLogoUrl}
                        alt={leftTeam}
                        style={{ width: 16, height: 16, objectFit: 'contain', marginTop: 2 }}
                        crossOrigin="anonymous"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </Box>

                  {/* Left Numbers Column(s) - single for 'single' mode, multiple for 'half'/'quarter' modes */}
                  {!hasMultipleNumberSets ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {/* For cell modes, render merged header cells with multiple numbers */}
                      {cellMode !== 'standard' ? (
                        Array.from({ length: visualRows }, (_, visualRowIndex) => {
                          const numbersInCell = getNumbersForMergedHeaderCell(leftNumbers, visualRowIndex, rowSpan);
                          return (
                            <Box
                              key={visualRowIndex}
                              sx={{
                                width: headerSize,
                                height: mergedCellHeight,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'stretch',
                                mb: '2px',
                              }}
                            >
                              {board.numbersRevealed ? (
                                numbersInCell.map((num, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: themeData.headerBackground,
                                      border: `1px solid ${themeData.cellBorder}`,
                                      borderBottom: i < numbersInCell.length - 1 ? 'none' : undefined,
                                      borderTop: i > 0 ? 'none' : undefined,
                                      color: leftColor,
                                      fontWeight: themeData.headerFontWeight,
                                      fontFamily: themeData.fontFamily,
                                      fontSize: '0.65rem',
                                    }}
                                  >
                                    {num}
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: themeData.headerBackground,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    color: leftColor,
                                    fontWeight: themeData.headerFontWeight,
                                    fontFamily: themeData.fontFamily,
                                  }}
                                >
                                  -
                                </Box>
                              )}
                            </Box>
                          );
                        })
                      ) : (
                        leftNumbers.map((num, row) => (
                          <Box
                            key={row}
                            sx={{
                              width: headerSize,
                              height: cellSize,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: themeData.headerBackground,
                              border: `1px solid ${themeData.cellBorder}`,
                              color: leftColor,
                              fontWeight: themeData.headerFontWeight,
                              fontFamily: themeData.fontFamily,
                              fontSize: '0.65rem',
                              mb: '2px',
                            }}
                          >
                            {board.numbersRevealed ? num : '-'}
                          </Box>
                        ))
                      )}
                    </Box>
                  ) : (
                    // Multi-number mode - reverse order so Q1/1H is closest to the grid (inside) and Q4/2H is furthest (outside)
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      {[...numberSetKeys].reverse().map((key) => {
                        const numbersForKey = getLeftNumbersForKey(key);
                        return (
                          <Box key={key} sx={{ display: 'flex', flexDirection: 'column', mr: '1px' }}>
                            {/* Period label at top of this column */}
                            <Box
                              sx={{
                                width: headerSize * 0.7,
                                height: headerSize * 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.45rem',
                                fontWeight: 'bold',
                                color: leftColor,
                                mb: '1px',
                              }}
                            >
                              {key}
                            </Box>
                            {/* For cell modes, render merged header cells with multiple numbers */}
                            {cellMode !== 'standard' ? (
                              Array.from({ length: visualRows }, (_, visualRowIndex) => {
                                const numbersInCell = getNumbersForMergedHeaderCell(numbersForKey, visualRowIndex, rowSpan);
                                return (
                                  <Box
                                    key={visualRowIndex}
                                    sx={{
                                      width: headerSize * 0.7,
                                      height: mergedCellHeight,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                      justifyContent: 'stretch',
                                      mb: '2px',
                                    }}
                                  >
                                    {board.numbersRevealed ? (
                                      numbersInCell.map((num, i) => (
                                        <Box
                                          key={i}
                                          sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: themeData.headerBackground,
                                            border: `1px solid ${themeData.cellBorder}`,
                                            borderBottom: i < numbersInCell.length - 1 ? 'none' : undefined,
                                            borderTop: i > 0 ? 'none' : undefined,
                                            color: leftColor,
                                            fontWeight: themeData.headerFontWeight,
                                            fontFamily: themeData.fontFamily,
                                            fontSize: '0.5rem',
                                          }}
                                        >
                                          {num}
                                        </Box>
                                      ))
                                    ) : (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: themeData.headerBackground,
                                          border: `1px solid ${themeData.cellBorder}`,
                                          color: leftColor,
                                          fontWeight: themeData.headerFontWeight,
                                          fontFamily: themeData.fontFamily,
                                        }}
                                      >
                                        -
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })
                            ) : (
                              numbersForKey.map((num, row) => (
                                <Box
                                  key={row}
                                  sx={{
                                    width: headerSize * 0.7,
                                    height: cellSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: themeData.headerBackground,
                                    border: `1px solid ${themeData.cellBorder}`,
                                    color: leftColor,
                                    fontWeight: themeData.headerFontWeight,
                                    fontFamily: themeData.fontFamily,
                                    fontSize: '0.5rem',
                                    mb: '2px',
                                  }}
                                >
                                  {board.numbersRevealed ? num : '-'}
                                </Box>
                              ))
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Grid Cells */}
                  <Box>
                    {/* Spacer to align with left column period labels in multi-number mode */}
                    {hasMultipleNumberSets && (
                      <Box sx={{ height: headerSize * 0.5 + 1, mb: '1px' }} />
                    )}
                    {/* For cell modes, render merged cells */}
                    {cellMode !== 'standard' ? (
                      Array.from({ length: visualRows }, (_, visualRowIndex) => (
                        <Box key={visualRowIndex} sx={{ display: 'flex', mb: '2px' }}>
                          {Array.from({ length: visualCols }, (_, visualColIndex) => {
                            // Get the first square position to determine owner
                            const { row: firstRow, col: firstCol } = getFirstSquareForVisualPosition(
                              visualRowIndex,
                              visualColIndex,
                              cellMode,
                              cellShape
                            );
                            const square = grid[firstRow]?.[firstCol];

                            // Sum hit counts for all squares in this cell
                            const cellPositions = getSquarePositionsForVisualCell(
                              visualRowIndex,
                              visualColIndex,
                              cellMode,
                              cellShape
                            );
                            const hitCount = cellPositions.reduce(
                              (sum, pos) => sum + getHitCount(pos.row, pos.col),
                              0
                            );

                            const bgColor = getSquareColor(square, firstRow, firstCol);
                            const isUnassigned = !square?.ownerId && !square?.offlinePlayerId;
                            const textColor = isUnassigned ? '#999999' : getContrastColor(bgColor);

                            // Cell group border style - use actual border for html2canvas compatibility
                            const cellGroupBorderColor = themeData.cellGroupBorderColor || 'rgba(0, 0, 0, 0.3)';
                            const cellGroupBorderWidth = themeData.cellGroupBorderWidth || 2;

                            return (
                              <Paper
                                key={visualColIndex}
                                elevation={0}
                                sx={{
                                  width: mergedCellWidth,
                                  height: mergedCellHeight,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: bgColor,
                                  border: `${cellGroupBorderWidth}px solid ${cellGroupBorderColor}`,
                                  borderRadius: `${themeData.cellBorderRadius}px`,
                                  ml: '2px',
                                  position: 'relative',
                                }}
                              >
                                {hitCount > 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      backgroundColor: themeData.winnerBadgeBackground,
                                      color: themeData.winnerBadgeTextColor,
                                      borderRadius: '50%',
                                      width: 14,
                                      height: 14,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.5rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {hitCount}
                                  </Box>
                                )}
                                {(() => {
                                  const { icon, handle } = getSquareLabel(square, firstRow, firstCol);
                                  // Scale font sizes up for larger merged cells
                                  const iconSize = cellMode === 'quad' ? '1.2rem' : '0.9rem';
                                  const handleSize = icon ? (cellMode === 'quad' ? '0.5rem' : '0.4rem') : (cellMode === 'quad' ? '0.7rem' : '0.55rem');
                                  return (
                                    <Box
                                      sx={{
                                        color: textColor,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {icon && <Box sx={{ display: 'flex', fontSize: iconSize }}>{icon}</Box>}
                                      {handle && (
                                        <Typography
                                          sx={{
                                            fontSize: handleSize,
                                            fontWeight: 'bold',
                                            lineHeight: 1,
                                            color: 'inherit',
                                          }}
                                        >
                                          {handle}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Paper>
                            );
                          })}
                        </Box>
                      ))
                    ) : (
                      grid.map((row, rowIndex) => (
                        <Box key={rowIndex} sx={{ display: 'flex', mb: '2px' }}>
                          {row.map((square, colIndex) => {
                            const hitCount = getHitCount(rowIndex, colIndex);
                            const bgColor = getSquareColor(square, rowIndex, colIndex);
                            // Use grey for unassigned squares to differentiate from claimed
                            const isUnassigned = !square?.ownerId && !square?.offlinePlayerId;
                            const textColor = isUnassigned ? '#999999' : getContrastColor(bgColor);

                            // Get box neighbor border style if enabled
                            const boxNeighborConfig = board.payoutConfig?.boxNeighborPayouts;
                            const defaultBorder = `1px solid ${themeData.cellBorder}`;
                            const boxBorderStyle = boxNeighborConfig?.enabled
                              ? getBoxBorderStyle(
                                  rowIndex,
                                  colIndex,
                                  boxNeighborConfig.boxSize,
                                  themeData.boxNeighborBorderColor || '#1976d2',
                                  themeData.boxNeighborBorderWidth || 2,
                                  defaultBorder
                                )
                              : {};

                            return (
                              <Paper
                                key={colIndex}
                                elevation={0}
                                sx={{
                                  width: cellSize,
                                  height: cellSize,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: bgColor,
                                  border: boxNeighborConfig?.enabled ? undefined : defaultBorder,
                                  borderRadius: `${themeData.cellBorderRadius}px`,
                                  ml: '2px',
                                  position: 'relative',
                                  ...(boxBorderStyle as object),
                                }}
                              >
                                {hitCount > 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 1,
                                      right: 1,
                                      backgroundColor: themeData.winnerBadgeBackground,
                                      color: themeData.winnerBadgeTextColor,
                                      borderRadius: '50%',
                                      width: 12,
                                      height: 12,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.45rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {hitCount}
                                  </Box>
                                )}
                                {(() => {
                                  const { icon, handle } = getSquareLabel(square, rowIndex, colIndex);
                                  return (
                                    <Box
                                      sx={{
                                        color: textColor,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {icon && <Box sx={{ display: 'flex', fontSize: '0.7rem' }}>{icon}</Box>}
                                      {handle && (
                                        <Typography
                                          sx={{
                                            fontSize: icon ? '0.35rem' : '0.5rem',
                                            fontWeight: 'bold',
                                            lineHeight: 1,
                                            color: 'inherit',
                                          }}
                                        >
                                          {handle}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Paper>
                            );
                          })}
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: `1px solid ${footerBorder}`, position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ color: footerText, fontSize: '0.55rem' }}>
                Powered by
              </Typography>
              <Box
                component="img"
                src={logoPng}
                alt="SquareUp"
                sx={{ height: 16, opacity: isLightText ? 1 : 0.7 }}
              />
            </Box>
            <Typography sx={{ color: footerText, fontSize: '0.55rem' }}>
              Exported {formatExportDate()}
            </Typography>
          </Box>
          </>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};
